import { useMemo } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { extractSitePlan, SitePlanBuilding, FloorPlanImage } from "../utils/floorPlanHelpers";

/**
 * Reads site_plan data from propertyPageData.data.site_plan.
 * Also picks up property-level floor_plan images that aren't associated with any room type
 * (the same images the gallery shows as "Property Layout").
 */
const useSitePlanData = (): {
  sitePlan: SitePlanBuilding[];
  hasSitePlan: boolean;
  propertyLayoutImages: FloorPlanImage[];
  hasPropertyLayout: boolean;
} => {
  const sitePlanRaw = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData.data?.site_plan,
  );

  const propertyImages = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData.data?.images,
  );

  const childrenShort = useSelector(
    ({ propertyPageData }: AppState) => propertyPageData.data?.children_short,
  );

  const sitePlan = useMemo(() => {
    if (sitePlanRaw && Array.isArray(sitePlanRaw) && sitePlanRaw.length > 0) {
      return extractSitePlan(sitePlanRaw);
    }
    return [];
  }, [sitePlanRaw]);

  // Property-level floor_plan images not associated with any room type.
  // These are the same images the gallery shows as "Property Layout".
  const propertyLayoutImages: FloorPlanImage[] = useMemo(() => {
    if (!propertyImages || !Array.isArray(propertyImages)) return [];

    // Collect all room-type image base_paths to exclude them
    const roomTypeImagePaths = new Set<string>();
    if (childrenShort && Array.isArray(childrenShort)) {
      childrenShort.forEach((child: any) => {
        (child.images || []).forEach((img: any) => {
          if (img.base_path) roomTypeImagePaths.add(img.base_path);
        });
      });
    }

    // Get floor_plan images from property-level that aren't in any room type
    return propertyImages
      .filter(
        (img: any) =>
          (img?.type ?? "").toLowerCase() === "floor_plan" &&
          !roomTypeImagePaths.has(img.base_path),
      )
      .map((img: any) => ({
        base_path: img.base_path,
        path: img.path || img.base_path,
        type: "floor_plan" as const,
      }));
  }, [propertyImages, childrenShort]);

  const hasSitePlan = sitePlan.length > 0;
  const hasPropertyLayout = propertyLayoutImages.length > 0;

  return { sitePlan, hasSitePlan, propertyLayoutImages, hasPropertyLayout };
};

export default useSitePlanData;
